import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sux1Logo } from "@/components/Sux1Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PublicStats } from "@/components/PublicStats";
import { BannerCarousel } from "@/components/BannerCarousel";
import { trackDemoViewed } from "@/lib/analytics";
import {
  BookOpen,
  FileText,
  GraduationCap,
  ScrollText,
  KeyRound,
  Globe,
  Highlighter,
  Sliders,
  Camera,
  Play,
  Sparkles,
  ArrowRight,
} from "lucide-react";

/**
 * Public demo landing (route "/demo").
 * A CSS-only animated "video" that cycles through 3 concrete examples:
 * school textbook · literature · press article.
 * No autoplaying <video> element — animation costs 0 KB of network.
 */

const EXAMPLES = [
  {
    key: "school",
    tag: "Manuel scolaire",
    before:
      "La Révolution française marque un tournant décisif dans l'histoire de l'Europe. Débutée en 1789 avec la prise de la Bastille, elle traduit le refus populaire d'un système féodal et monarchique jugé profondément inégalitaire. Sur près d'une décennie, elle abolit les privilèges de la noblesse, proclame la Déclaration des droits de l'homme et du citoyen (26 août 1789) et instaure les fondements d'un régime républicain fondé sur la souveraineté populaire. Ses idéaux — liberté, égalité, fraternité — inspireront durablement toutes les démocraties occidentales.",
    after:
      "La Révolution française (1789-1799) abolit le système féodal, proclame les Droits de l'homme et pose les fondations d'un régime républicain. Ses idéaux de liberté, égalité et fraternité inspireront durablement les démocraties occidentales.",
    stats: { in: 623, out: 194, time: "8 s" },
  },
  {
    key: "literature",
    tag: "Littérature",
    before:
      "Longtemps, je me suis couché de bonne heure. Parfois, à peine ma bougie éteinte, mes yeux se fermaient si vite que je n'avais pas le temps de me dire : « Je m'endors. » Et, une demi-heure après, la pensée qu'il était temps de chercher le sommeil m'éveillait ; je voulais poser le volume que je croyais avoir encore dans les mains et souffler ma lumière ; je n'avais pas cessé en dormant de faire des réflexions sur ce que je venais de lire, mais ces réflexions avaient pris un tour un peu particulier.",
    after:
      "Le narrateur évoque ses couchers précoces où le sommeil vient si vite qu'il continue en rêve les pensées de sa lecture, brouillant la frontière entre veille et sommeil.",
    stats: { in: 522, out: 158, time: "6 s" },
  },
  {
    key: "press",
    tag: "Article de presse",
    before:
      "Le prix du baril de Brent a franchi hier soir la barre symbolique des 95 dollars, portant sa hausse à plus de 12 % depuis le début du mois. Cette flambée s'explique par la conjonction de trois facteurs : la baisse de production annoncée par l'OPEP+, les tensions renouvelées au Moyen-Orient, et un dollar affaibli face à l'euro. Les économistes préviennent qu'un maintien durable au-dessus de 100 $ pèserait mécaniquement sur les prix à la pompe en Europe dès novembre.",
    after:
      "Le baril de Brent dépasse 95 $ (+12 % ce mois), tiré par la baisse OPEP+, les tensions au Moyen-Orient et un dollar faible. Impact prévu à la pompe en Europe dès novembre.",
    stats: { in: 496, out: 152, time: "5 s" },
  },
];

const FEATURES = [
  { icon: BookOpen,       title: "Résumé de livres",           body: "PDF, EPUB, DOCX — même 800 pages, découpé chapitre par chapitre." },
  { icon: FileText,       title: "Résumé de PDF",              body: "Articles, rapports, thèses téléversés en un glisser-déposer." },
  { icon: GraduationCap,  title: "Résumé de cours",            body: "Notes de cours et polycopiés reformulés pour vos révisions." },
  { icon: ScrollText,     title: "Résumé de thèses",           body: "Mémoires et articles scientifiques synthétisés avec fidélité." },
  { icon: KeyRound,       title: "Idées principales",          body: "Extraction automatique des concepts et arguments clés." },
  { icon: Sliders,        title: "Court ou détaillé",          body: "Choisissez la longueur : bref pour un tweet, long pour un exposé." },
  { icon: Highlighter,    title: "Points clés & mots-clés",    body: "Format « bullet points » idéal pour projection en classe." },
  { icon: Globe,          title: "Multilingue",                body: "6 langues supportées + détection automatique de la source." },
  { icon: Camera,         title: "Scan de couverture",         body: "Prenez un livre en photo, obtenez sa fiche de lecture complète." },
];

export default function Demo() {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const ex = EXAMPLES[idx];

  // Cycle through examples every 9 seconds (matches CSS animation length).
  useEffect(() => {
    trackDemoViewed();
    const id = setInterval(() => setIdx((i) => (i + 1) % EXAMPLES.length), 9000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* ==== Local CSS animations (scoped via unique classnames) ==== */}
      <style>{`
        @keyframes sux1-slide-in { from { opacity: 0; transform: translateY(12px);} to { opacity: 1; transform: translateY(0);} }
        @keyframes sux1-pulse-blue { 0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0,47,167,0.5);} 50% { transform: scale(1.08); box-shadow: 0 0 0 18px rgba(0,47,167,0);} }
        @keyframes sux1-reveal { from { clip-path: inset(0 100% 0 0);} to { clip-path: inset(0 0 0 0);} }
        @keyframes sux1-scan { 0% { left: -3px; } 100% { left: 100%; } }
        .sux1-fade-in { animation: sux1-slide-in 0.5s ease-out both; }
        .sux1-pulse { animation: sux1-pulse-blue 2.2s ease-out infinite; }
        .sux1-reveal-wrap { position: relative; overflow: hidden; }
        .sux1-reveal { animation: sux1-reveal 2.0s cubic-bezier(0.65, 0, 0.35, 1) both; animation-delay: 0.8s; }
        .sux1-scan-line {
          position: absolute; top: 0; bottom: 0; width: 3px; background: #FF2A00;
          box-shadow: 0 0 12px 2px rgba(255, 42, 0, 0.7), 0 0 30px 4px rgba(255, 42, 0, 0.35);
          animation: sux1-scan 2.0s cubic-bezier(0.65, 0, 0.35, 1) both;
          animation-delay: 0.8s;
          pointer-events: none;
          left: -3px;
        }
      `}</style>

      {/* ===== Header ===== */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <Sux1Logo size="md" />
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <LanguageSwitcher />
            <Link
              to="/signup"
              data-testid="demo-header-signup"
              className="font-mono uppercase tracking-[0.16em] text-[11px] px-4 py-2 bg-[#002FA7] text-white hover:bg-[#00227A] transition-colors"
            >
              S'inscrire · gratuit
            </Link>
          </div>
        </div>
      </header>

      {/* ===== Value proposition ===== */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-10 text-center">
        <div className="sux-marker mb-4 inline-flex items-center gap-2 justify-center">
          <Sparkles className="w-3 h-3" /> Démonstration
        </div>
        <h1
          className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] mb-6"
          style={{ fontFamily: "Outfit" }}
          data-testid="demo-headline"
        >
          Le résumeur IA
          <br />
          <span className="text-[#002FA7]">français.</span>
        </h1>
        <p className="text-lg lg:text-2xl text-muted-foreground max-w-3xl mx-auto leading-snug">
          <strong className="text-foreground">3 modèles, 1 seul tarif : dès 2 € les 20 résumés.</strong>
          {" "}
          Résumez tout. Comprenez plus vite.
        </p>

        <div className="mt-8 flex items-center gap-3 justify-center flex-wrap">
          <Link
            to="/signup"
            data-testid="demo-hero-signup"
            className="inline-flex items-center gap-2 bg-[#002FA7] text-white font-mono uppercase tracking-[0.2em] text-xs px-6 py-4 hover:bg-[#00227A] transition-colors"
          >
            Essayer SUX1 <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link
            to="/login"
            data-testid="demo-hero-login"
            className="inline-flex items-center gap-2 border border-border text-foreground font-mono uppercase tracking-[0.2em] text-xs px-6 py-4 hover:bg-[#FF2A00] hover:text-white transition-colors"
          >
            J'ai déjà un compte
          </Link>
        </div>

        <div className="mt-6 flex justify-center">
          <PublicStats />
        </div>
      </section>

      {/* ===== Animated demo ===== */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-stretch">
          {/* AVANT */}
          <div
            key={`before-${ex.key}`}
            data-testid="demo-before-card"
            className="sux1-fade-in border border-border bg-background p-6 md:p-8 min-h-[280px] md:min-h-[340px] flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="sux-marker">Avant</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 bg-[#002FA7]/5">
                {ex.tag}
              </span>
            </div>
            <div className="flex-1 text-sm leading-relaxed text-muted-foreground">
              {ex.before}
            </div>
            <div className="mt-4 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-t border-border pt-3">
              <span>{ex.stats.in} caractères</span>
            </div>
          </div>

          {/* SUX1 icon in the middle */}
          <div className="flex flex-col items-center justify-center py-4 md:py-0">
            <div className="sux1-pulse flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-[#002FA7] text-white">
              <Sparkles className="w-6 h-6" />
            </div>
            <span className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-[#002FA7]">
              SUX1 analyse
            </span>
            <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              ~ {ex.stats.time}
            </span>
          </div>

          {/* APRÈS */}
          <div
            key={`after-${ex.key}`}
            data-testid="demo-after-card"
            className="sux1-fade-in border-2 border-[#002FA7] bg-[#002FA7]/[0.03] p-6 md:p-8 min-h-[280px] md:min-h-[340px] flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="sux-marker text-[#002FA7]">Après</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 bg-[#002FA7] text-white">
                Résumé SUX1
              </span>
            </div>
            <div className="sux1-reveal-wrap flex-1">
              <div
                className="sux1-reveal text-base leading-relaxed text-foreground font-medium"
                style={{ fontFamily: "Outfit" }}
              >
                {ex.after}
              </div>
              <div className="sux1-scan-line" aria-hidden="true" />
            </div>
            <div className="mt-4 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.14em] text-[#002FA7] border-t border-[#002FA7]/20 pt-3">
              <span>{ex.stats.out} caractères</span>
              <span>·</span>
              <span>{Math.round(100 - (ex.stats.out / ex.stats.in) * 100)} % plus court</span>
            </div>
          </div>
        </div>

        {/* Progress dots */}
        <div className="mt-8 flex items-center justify-center gap-2" data-testid="demo-progress-dots">
          {EXAMPLES.map((e, i) => (
            <button
              key={e.key}
              onClick={() => setIdx(i)}
              data-testid={`demo-dot-${e.key}`}
              aria-label={`Voir l'exemple : ${e.tag}`}
              className={`h-1.5 transition-all ${
                i === idx ? "w-8 bg-[#002FA7]" : "w-3 bg-border hover:bg-[#FF2A00]/30"
              }`}
            />
          ))}
        </div>
      </section>

      {/* ===== Banner carousel — deep brand storytelling ===== */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14 border-t border-border">
        <div className="text-center mb-8">
          <div className="sux-marker mb-3">Présentation</div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
            Tout SUX1 en 7 bannières.
          </h2>
          <p className="text-muted-foreground mt-3 text-sm">
            Défilement automatique toutes les 10 secondes — passez la souris pour mettre en pause.
          </p>
        </div>
        <BannerCarousel />
      </section>

      {/* ===== Features grid ===== */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 border-t border-border">
        <div className="text-center mb-12">
          <div className="sux-marker mb-3">Fonctionnalités</div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
            Tout ce qui compte, en un seul outil.
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
            Neuf capacités qui remplacent une bibliothèque entière d'applications.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-border">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              data-testid={`demo-feature-${i}`}
              className="border-r border-b border-border p-6 lg:p-8 hover:bg-[#FF2A00]/[0.02] transition-colors"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-[#002FA7] text-white mb-4">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-black text-lg mb-2" style={{ fontFamily: "Outfit" }}>
                {f.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Bottom CTA ===== */}
      <section className="border-t border-border bg-[#002FA7] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 text-center">
          <h2 className="text-4xl lg:text-5xl font-black tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>
            Commencez maintenant.
            <br />
            <span className="text-[#8BA1E8]">C'est gratuit.</span>
          </h2>
          <p className="text-white/70 mb-8 max-w-lg mx-auto">
            Exemples gratuits. Puis dès 2 € les 20 résumés — crédits sans expiration, jamais d'abonnement.
          </p>
          <Link
            to="/signup"
            data-testid="demo-footer-signup"
            className="inline-flex items-center gap-2 bg-[#002FA7] text-white font-mono uppercase tracking-[0.2em] text-xs px-8 py-5 hover:bg-[#00227A] transition-colors"
          >
            Créer mon compte <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center gap-4 justify-between">
          <span className="sux-marker">SUX1 · Made in France</span>
          <div className="flex items-center gap-6 font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground">
            <Link to="/about" className="hover:text-foreground">À propos</Link>
            <Link to="/privacy" className="hover:text-foreground">Confidentialité</Link>
            <a href="mailto:fiestamax@gmail.com" className="hover:text-foreground">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
